import React, { useState, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { learningGroupService, LearningGroup } from '../../services/learningGroupService';
import MaterialSymbol from '../common/MaterialSymbol';

interface MyGroupsWidgetProps {
  limit?: number;
}

/**
 * "Meine Lerngruppen" — Panel im neuen Design (.grouprow).
 * Gleicher Datenabruf (learningGroupService.getMyGroups) und gleiche
 * Navigation (/learning-groups, /learning-groups/:id) wie zuvor.
 */
const MyGroupsWidget: React.FC<MyGroupsWidgetProps> = ({ limit = 3 }) => {
  const [groups, setGroups] = useState<LearningGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const translate = useTranslate();
  const navigate = useNavigate();

  useEffect(() => {
    loadMyGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMyGroups = async () => {
    try {
      const data = await learningGroupService.getMyGroups();
      setGroups(data.slice(0, limit));
    } catch (error) {
      console.error('Error loading groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="panel">
        <div className="skel" />
        <div className="skel" />
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="sec-head">
        <h2 className="sec-title">
          <span className="ico green"><MaterialSymbol name="groups" size={19} /></span>
          {translate('learningGroups.myGroups', 'Meine Lerngruppen')}
        </h2>
        <button type="button" className="seeall" onClick={() => navigate('/learning-groups')}>
          {translate('common.viewAll', 'Alle anzeigen')}
          <MaterialSymbol name="arrow_forward" size={15} />
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="empty-note">
          <p>{translate('learningGroups.noMyGroups', 'Sie sind noch keiner Lerngruppe beigetreten')}</p>
          <button type="button" className="btn-out" onClick={() => navigate('/learning-groups')}>
            {translate('learningGroups.explore', 'Gruppen entdecken')}
          </button>
        </div>
      ) : (
        groups.map((group, index) => (
          <div
            className="grouprow"
            key={group.id}
            onClick={() => navigate(`/learning-groups/${group.id}`)}
          >
            <span className={`gav${index % 2 === 1 ? ' alt' : ''}`}>{getInitials(group.name)}</span>
            <div className="body">
              <div className="ttl">{group.name}</div>
              <div className="meta">
                <span className="members">
                  <MaterialSymbol name="person" size={14} />
                  {translate('learningGroups.members', {
                    _: '%{count} Mitglieder',
                    count: group.memberCount || 0,
                  })}
                </span>
                {group.courseTitle && <span className="chip-out">{group.courseTitle}</span>}
              </div>
            </div>
            <span className="go"><MaterialSymbol name="arrow_forward" size={20} /></span>
          </div>
        ))
      )}
    </div>
  );
};

export default MyGroupsWidget;
